import React from 'react';
import FullPageLoader from 'components/loaders/FullPageLoader';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import MockServicePreviewDataSample from './ContentPane/MockServicePreviewDataSample';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import NoContentPlaceholder from 'components/ui/noContentPlaceholder/NoContentPlaceholder';
import { Link } from 'react-router-dom';
import styles from 'views/MockServices/Tabs/MockTemplates/MockTemplatesListBlazeTable.module.css';
import _ from 'lodash';
import blazeDataClient from 'blazeData/blazeDataClient';
import { Csv, DataModel, MeModel } from '@blazemeter/blaze-data-model';
import { setPageFilter } from 'store/ui/pageFilters/pageFilterActions';
import { setDataEntitySelected, toggleOpenDDPanel } from 'store/ui/ddActions';
import { ManipulatorDataModel, MeManipulatorDM } from '@blazemeter/blaze-data-designer-client';
import { GlobalScopeVars, IterationOverrides } from 'types/rest/blazeDataTypes';
import { MockService } from 'types/rest/mockServiceTypes';
import { getTableDataFromObject } from 'utils/tableEditorHelper';
import {
  countEntitiesInDataModel,
  extractPropertyGroupsFromManipulatorModel,
  getModelComplexity,
  getPreviewTitleSuffix,
  isBlazeDataLimitApplied,
  isComplexityOverThreshold,
  isModelDifferent,
  mergeOverrideWithDataModel,
} from 'utils/blazeDataUtils';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import axios from 'axios/axios-base';
import { DataPreview, DataPreviewSettings, IData, PropertyGroupFromDataModel } from '@blazemeter/blaze-data-ui';
import { getErrorMessage, getFriendlyNumberFormat, isTemplate } from 'utils/miscUtil';
import Button from 'react-bootstrap/Button';
import { createDraft, getCurrentMockServiceOrDraft } from 'store/mockServices/mockServiceList/mockServiceListActions';
import { createTemplateDraft } from 'store/mockServiceTemplates/mockServiceTemplatesActions';
import i18n from 'i18n';
import {
  getAssetData,
  getDataModelForService,
  getGlobalEntityAssetByGlobalEntityName,
  loadSharedFolderCsvFiles,
} from 'store/dataModel/dataModelActions';
import { DetailTabKey } from 'store/mockServices/mockServiceTypes';
import { getActiveWorkspaceId, getBlazemeterBillingPageUrl } from 'utils/bzmUserConfigUtil';
import MixpanelService from 'utils/mixpanelService';
import {
  APPLY_CHANGES_FROM_BLAZE_DATA_MODEL,
  DATA_SETTINGS_OPEN,
  OPEN_BLAZE_DATA_PANEL_FROM_MOCK_SERVICE,
} from 'utils/mixpanelEvents';
import BzmIcon from 'components/icons/BzmIcon';
import BlazeDataEntitiesAccordion, {
  GLOBAL_SCOPE_KEY,
  SQL_BOOTSTRAP_SCRIPT,
} from './Accordion/BlazeDataEntitiesAccordion';
import GlobalScopePreview from './ContentPane/GlobalScopePreview';
import { generateGlobalEntityName } from 'blazeData/blazeDataUtils';
import { pollTrackingService } from 'axios/axios-polling';
import acApi from 'axios/ac/acApi';
import RefreshBtn from './Buttons/RefreshBtn';
import SqlBootstrapForm from '../MockServiceDetail/SqlBootstrapTab';

export interface MockServiceDataSettingsProps {
  mockServiceEntity: MockService | MockServiceTemplate;
  /** When publish configuration gets updated */
  onUpdateIterationOverrides: (iterationOverrides: IterationOverrides) => void;
  disabled: boolean;
  /** Changes tab different one from data panel */
  onTabChange?: (name: DetailTabKey) => void;
  isMsStateful?: boolean;
  isMsRunning?: boolean;
}

export interface MSDataPreview extends DataPreview {
  /** Indicates whether the data has been deployed (= they are fixed and will not change before next redeploy happens) */
  isFixed: boolean;
  downloadLink?: string;
}

export interface DataSettingsState {
  isFetchingDataModel: boolean;
  selectedDataModel: DataModel | null; //TODO - do not store whole model, just pointer here...
  selectedManipulatorDataModel: ManipulatorDataModel | null; // Manipulator model contains default iteration settings
  isGeneratingDataPreview: boolean; //When data are being fetched from the backend
  isPreviewListValid: boolean;
  dataPreviews: {
    [key: string]: MSDataPreview; // Data previews for all entities
  };
  globalScope: GlobalScopeVars; // Current "Global Entity"
  dataModels: {
    tdmModel: DataModel | null; // Current model in blaze data
    deployedModel: DataModel | null; // Snapshot of the model taken during deployment (loaded from shared folder)
    isDifferent: boolean; //Comparison of data models
  };
  isMsUsingBlazeData: null | boolean; // Indicates if virtual service is using ${templates} in the transactions
  isComplex: null | boolean; //Determines whether model complexity exceeds threshold
  blazeDataLimit?: number; // Can differ per account
  isLimitApplied?: boolean; // Tells user, if we truncated the data (it exceeded max limit)
  blazeDataLinks: {
    previewFileLink?: string; // For showing the table preview
    modelFileLink?: string; // For comparison with current model
    dataFileLink?: string; // For download
  };
  isExportInProgress: boolean; // Async blazeData export from Virtual Service to AC
  isError: boolean; // For showing error placeholder
}

type AllProps = MapToStateProps & DispatchProps & MockServiceDataSettingsProps & WithTranslation;

const getDefaultDataPreview = (isFixed: boolean): MSDataPreview => ({
  rows: [],
  settings: { editable: false, title: '' },
  isFixed: isFixed,
  tableHeaders: null,
});

export class MockServiceDataSettings extends React.Component<AllProps, DataSettingsState> {
  /**
   * Data should be fixed in case the mock has been deployed already, and we are not
   * in the draft mode.
   */
  isFixedDataShown = (): boolean => {
    if (isTemplate(this.props.mockServiceEntity)) {
      return false;
    }

    return (
      (this.props.mockServiceEntity as MockService).status === 'RUNNING' &&
      !this.props.mockServiceEntity.isNew &&
      !this.props.mockServiceEntity.isDraft
    );
  };

  state: DataSettingsState = {
    globalScope: null,
    isFetchingDataModel: true,
    selectedManipulatorDataModel: null,
    isGeneratingDataPreview: false,
    isPreviewListValid: true,
    dataPreviews: {},
    dataModels: {
      tdmModel: null,
      deployedModel: null,
      isDifferent: false,
    },
    isMsUsingBlazeData: null,
    isComplex: null,
    selectedDataModel: null,
    blazeDataLinks: {},
    isExportInProgress: false,
    isError: false,
  };

  componentDidUpdate(prevProps: Readonly<AllProps>) {
    const prevMsEntity: any = prevProps.mockServiceEntity;
    const currentMsEntity: any = this.props.mockServiceEntity;

    const serviceHasChanged = prevMsEntity.serviceId !== currentMsEntity.serviceId;
    const previewLinksChanged = prevMsEntity.blazeDataPreviewFile !== currentMsEntity.blazeDataPreviewFile;
    const isReconfigured =
      currentMsEntity.status === 'RUNNING' && prevMsEntity.configuredAt !== currentMsEntity.configuredAt;
    const metaChanged =
      _.get(prevMsEntity, 'metaData.isStopped') !== _.get(currentMsEntity, 'metaData.isStopped') ||
      _.get(prevMsEntity, 'metaData.isDeployed') !== _.get(currentMsEntity, 'metaData.isDeployed') ||
      _.get(prevMsEntity, 'metaData.isFailed') !== _.get(currentMsEntity, 'metaData.isFailed');

    //Re-fetch data models if one of those conditions changes
    if (serviceHasChanged || previewLinksChanged || isReconfigured || metaChanged) {
      this.fetchAllDataDebounced();
    }
  }

  setCurrentEntity = (entity: string) => {
    this.props.setDataEntitySelected(entity);
  };

  componentDidMount(): void {
    this.fetchAllData();
    MixpanelService.track(DATA_SETTINGS_OPEN());
  }

  /**
   * Starts fetching global scope & data model.
   */
  fetchAllData = async (keepSelectedEntity?: boolean) => {
    if (!keepSelectedEntity) {
      await this.setState({ dataPreviews: {} });
    }
    await this.fetchGlobalScope();
    await this.fetchDataModels(keepSelectedEntity);
  };

  /**
   * Fetches virtual service object representation from backend and then
   * checks if this virtual service / draft is using blaze data templates within transactions
   * and stores file links to local state.
   */
  getFileLinksAndTemplateUsage = async () => {
    const { mockServiceEntity } = this.props;
    if (!isTemplate(mockServiceEntity)) {
      const ms: MockService = await getCurrentMockServiceOrDraft(mockServiceEntity as MockService);

      //Before storing new preview link, check if it differs from the last stored value
      if (this.state.blazeDataLinks.previewFileLink !== _.get(ms, 'blazeDataLinks.previewFileLink')) {
        this.setState({ isPreviewListValid: false });
      }

      this.setState({
        isMsUsingBlazeData: Boolean(ms.metadata?.badges?.CONTAINS_EXPRESSIONS).valueOf(),
        blazeDataLinks: {
          previewFileLink: _.get(ms, 'blazeDataLinks.previewFileLink'),
          modelFileLink: _.get(ms, 'blazeDataLinks.modelFileLink'),
          dataFileLink: _.get(ms, 'blazeDataLinks.dataFileLink'),
        },
      });
    }
  };

  /**
   * If we are in draft mode, the preview is generated from blaze data backend based on the iteration settings. In case
   * we are rendering deployed virtual service (aka original) we will show data, which is used by the mock (stored on shared folder
   * during the deployment).
   *
   */
  generateDataPreview = async (forceRegenerate?: boolean, entityId?: string) => {
    // Check if there is not already preview for current entity,
    // if yes, just use it, dont generate new
    const activeEntity = entityId ?? this.props.dataEntitySelected;
    const preview = _.get(this.state.dataPreviews, activeEntity);
    if (forceRegenerate !== true && !_.isNil(preview) && this.state.isPreviewListValid === true) {
      //Re-use cached value...
      return;
    }

    await this.setState({ isGeneratingDataPreview: true });
    //If ms is running, get preview from shared folder
    if (this.isFixedDataShown()) {
      await this.getDataPreviewFromSharedFolder(activeEntity);
    } else {
      await this.generateDataPreviewFromBlazeData(activeEntity);
    }
    await this.setState({ isGeneratingDataPreview: false, isPreviewListValid: true });
  };

  getModelFromSharedFolder = async () => {
    if (_.isNil(this.state.blazeDataLinks.modelFileLink)) {
      return;
    }
    try {
      const deployedBlazeModel = (await axios.get(this.state.blazeDataLinks.modelFileLink)).data;
      const stateUpdate = _.cloneDeep(this.state);
      stateUpdate.dataModels.deployedModel = deployedBlazeModel;
      this.setState(stateUpdate);
    } catch (error) {
      this.props.showToastNotification(
        ToastType.Danger,
        'Cannot get Data Model from Shared Folder',
        getErrorMessage(error),
      );
    }
  };

  /**
   * Generate Preview from Shared folder.
   */
  getDataPreviewFromSharedFolder = async (entityId?: string) => {
    if (_.isNil(this.state.blazeDataLinks.previewFileLink)) {
      return;
    }
    const activeEntity = entityId ?? this.props.dataEntitySelected;

    try {
      const blazeData = (await axios.get(this.state.blazeDataLinks.previewFileLink)).data;

      if (blazeData.isValid && !_.isNil(activeEntity)) {
        //Get data for currently selected entity from blaze data
        const generatedData = blazeData[activeEntity];
        const propertyGroups = extractPropertyGroupsFromManipulatorModel(
          this.state.selectedManipulatorDataModel as MeManipulatorDM,
          activeEntity,
        );
        this.createPreviewForCurrentEntity(activeEntity, generatedData.data, generatedData.total, propertyGroups);
      }
    } catch (error) {
      //Clear the preview for this entity
      let previews = _.cloneDeep(this.state.dataPreviews);
      previews = _.set(previews, activeEntity, getDefaultDataPreview(this.isFixedDataShown()));
      await this.setState({ dataPreviews: previews });
      this.props.showToastNotification(
        ToastType.Danger,
        'An error occurred while processing data from shared folder.',
        getErrorMessage(error),
      );
    }
  };

  /**
   * Gets data preview from blaze data backend (current data model).
   */
  generateDataPreviewFromBlazeData = async (entityId?: string) => {
    const activeEntity = entityId ?? this.props.dataEntitySelected;
    if (
      _.isNil(this.state.selectedManipulatorDataModel) ||
      _.isNil(this.state.selectedManipulatorDataModel.entities) ||
      _.isNil(activeEntity)
    ) {
      return;
    }

    const manipulator = blazeDataClient.manipulator;
    const preview = 100;

    try {
      const extractedModel: MeModel = (await manipulator.extractDataModel(
        this.state.selectedManipulatorDataModel,
        this.state.selectedManipulatorDataModel.entities[activeEntity].dataset.id,
      )) as MeModel;

      const { dataSettings } = this.props.mockServiceEntity;

      //If overrides exist, merge them with current extracted model
      if (dataSettings && dataSettings.data.attributes.iterationOverride) {
        extractedModel.entities = mergeOverrideWithDataModel(
          extractedModel.entities,
          _.get(dataSettings, 'data.attributes.iterationOverride.entities'),
        );
      }

      //Load shared Folder files
      const getState = () => this.props.appState;
      const files = await loadSharedFolderCsvFiles(getState, extractedModel);
      const csvFiles: Csv[] = files
        ? files.map(f => ({
            name: f.name,
            location: f.link || '',
            lastModified: f.lastModified,
          }))
        : [];

      //Generator generates random data for preview based on the model
      const generatedResult = await manipulator.generator.generateStoredModel(
        extractedModel.id,
        extractedModel,
        undefined,
        preview,
        activeEntity,
        csvFiles,
      );

      //Call generate with overrides
      if (generatedResult && generatedResult.isValid && generatedResult[activeEntity].data) {
        const propertyGroups = extractPropertyGroupsFromManipulatorModel(
          this.state.selectedManipulatorDataModel as MeManipulatorDM,
          activeEntity,
        );
        const entityData = generatedResult[activeEntity];
        this.createPreviewForCurrentEntity(activeEntity, entityData.data, entityData.total as number, propertyGroups);
      }

      //Check model complexity of newly generated data
      await this.checkModelComplexity();
      await this.setState({ isError: false });
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.result.report.report.message', getErrorMessage(error));
      this.props.showToastNotification(
        ToastType.Danger,
        'An error occurred during generation of the Blaze Data preview.',
        errorMsg,
      );
      await this.setState({ isError: true });
    } finally {
      await this.setState({ isGeneratingDataPreview: false });
    }
  };

  /**
   * Creates dto for CSV table.
   * @param generatedData
   * @param total
   * @param pgs
   */
  createPreviewForCurrentEntity = (
    activeEntity: string,
    generatedData: any,
    total: number,
    pgs: Array<PropertyGroupFromDataModel>,
  ) => {
    const previewTitle = getPreviewTitleSuffix(generatedData.length, total);
    const tableData = getTableDataFromObject(pgs, generatedData);
    const isFixed = this.isFixedDataShown();
    const preview = {
      settings: { editable: false, title: previewTitle } as DataPreviewSettings,
      rows: tableData.rows as IData,
      tableHeaders: tableData.tableHeaders,
      isFixed: isFixed,
      downloadLink: this.state.blazeDataLinks.dataFileLink,
    };

    let dataPreviews = _.cloneDeep(this.state.dataPreviews);
    dataPreviews = _.set(dataPreviews, activeEntity, preview);
    this.setState({ dataPreviews: dataPreviews });
  };

  fetchAllDataDebounced = _.debounce(this.fetchAllData, 500);

  /**
   * Gets global scope variables from Asset Repository.
   * */
  fetchGlobalScope = async () => {
    if (this.props.isMsStateful === true && this.isFixedDataShown()) {
      await this.fetchGlobalVariableSnapshot();
    } else {
      const globalEntityName = generateGlobalEntityName(this.props.mockServiceEntity.serviceId);
      const globalEntityAsset = await getGlobalEntityAssetByGlobalEntityName(globalEntityName);

      if (_.isNil(globalEntityAsset)) {
        //Add non empty global scope, so we disable loading indicator
        this.setState({ globalScope: {} });
        return;
      }

      const globalEntityData = await getAssetData(globalEntityAsset.id);
      if (_.isNil(globalEntityData) || _.isEmpty(globalEntityData)) {
        this.setState({ globalScope: {} });
        return;
      }

      if (!_.isNil(globalEntityAsset)) {
        this.setState({ globalScope: globalEntityData });
      }
    }
  };

  /**
   * Fetches global scope snapshot from the AC.
   */
  fetchGlobalVariableSnapshot = async () => {
    try {
      const response = await acApi.mockServices.getBlazeData(this.props.mockServiceEntity.id);
      const globalVariables = response.data.result.globalVariables;
      this.setState({ globalScope: !_.isNil(globalVariables) ? globalVariables : {} });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * We fetch both models from backend first and if there is deployed one available,
   * it is marked as selected in local state.
   * @param keepSelectedEntity - if set to true, we will not change entity selection after model is fetched.
   */
  fetchDataModels = async (keepSelectedEntity?: boolean) => {
    if (!keepSelectedEntity) {
      await this.setState({ isFetchingDataModel: true, selectedDataModel: null, selectedManipulatorDataModel: null });
    }

    // Gets file links (DE526295)
    await this.getFileLinksAndTemplateUsage();

    //Always get TDM model (it will be either used as active or used in comparison)
    await this.getModelFromTdm();
    let modelToBeProcessed = this.state.dataModels.tdmModel;

    //If MS is running and there is some data deployed, get it from shared folder
    if (this.isFixedDataShown()) {
      await this.getModelFromSharedFolder();
      modelToBeProcessed = this.state.dataModels.deployedModel;

      // Compare both models
      const isDeployedModelDifferent = isModelDifferent(
        this.state.dataModels.deployedModel,
        this.state.dataModels.tdmModel,
      );

      // Save result of comparison
      this.setState({
        ...this.state,
        dataModels: { ...this.state.dataModels, isDifferent: isDeployedModelDifferent },
      });
    }

    if (modelToBeProcessed) {
      await this.selectDataModel(modelToBeProcessed, keepSelectedEntity);
    }

    this.setState({ isFetchingDataModel: false, isGeneratingDataPreview: false });
  };

  /**
   * Checks model complexity from BlazeData backend.
   */
  checkModelComplexity = async () => {
    if (!_.isNil(this.state.dataModels.tdmModel)) {
      const id = this.state.dataModels.tdmModel.id;
      const workSpaceId = getActiveWorkspaceId();
      try {
        const dataSettings = _.get(this.props.mockServiceEntity, 'dataSettings');
        const complexity = await getModelComplexity(workSpaceId, id, dataSettings);
        this.setState({
          isComplex: isComplexityOverThreshold(_.get(complexity, 'result.totalCount')),
          isLimitApplied: isBlazeDataLimitApplied(complexity),
          blazeDataLimit: _.get(complexity, 'billingLimits.total'),
        });
      } catch (e) {
        console.log('Cannot get Blaze Data Complexity. ', getErrorMessage(e));
      }
    }
  };

  /**
   * Loads model assigned to currently selected virtual service from BlazeData backend.
   */
  getModelFromTdm = async () => {
    try {
      const dataModel = await getDataModelForService(this.props.mockServiceEntity.serviceId);
      this.setState({
        ...this.state,
        dataModels: { ...this.state.dataModels, tdmModel: dataModel ? dataModel : null },
      });
    } catch (error) {
      this.props.showToastNotification(
        ToastType.Danger,
        'An error occurred during communication with Blaze Data.',
        getErrorMessage(error),
      );
    }
  };

  /**
   * Selects data model and makes it active on UI.
   * @param dataModel - data model to be selected
   * @param keepSelectedEntity - optional boolean, if set to true we we will keep current entity selection.
   */
  selectDataModel = async (dataModel: DataModel, keepSelectedEntity?: boolean) => {
    //If there is any entity in the model, make the available first one active by default
    let activeEntity = this.props.dataEntitySelected;

    const entities = (dataModel as MeModel).entities;
    const entitiesCount = _.size(entities);

    // If there are some entities, select the first one available.
    // In case there are none but there is global scope available make it active.
    if (activeEntity === undefined) {
      if (entitiesCount !== 0) {
        activeEntity = Object.keys(entities)[0];
      } else if (!_.isEmpty(this.state.globalScope)) {
        activeEntity = GLOBAL_SCOPE_KEY;
      }
    }

    //Get Manipulator Model from TDM
    const manipulatorModel = await blazeDataClient.manipulator.load(dataModel);
    await this.setState({
      selectedDataModel: dataModel,
      selectedManipulatorDataModel: manipulatorModel,
    });
    this.props.setDataEntitySelected(activeEntity);

    //Generate preview if entity is selected
    if (activeEntity !== GLOBAL_SCOPE_KEY && activeEntity !== SQL_BOOTSTRAP_SCRIPT) {
      await this.generateDataPreview(true);
    }
  };

  openBlazeDataPanel = () => {
    MixpanelService.track(OPEN_BLAZE_DATA_PANEL_FROM_MOCK_SERVICE());
    //Select service
    this.props.setPageFilter({
      serviceId: this.props.mockServiceEntity.serviceId,
      searchTerm: '',
    });

    //Toggle data panel if necessary
    if (this.props.isDDPanelOpened === false) {
      this.props.toggleOpenDDPanel();
    }
  };

  getAddDataButton = () => {
    return (
      <Link onClick={this.openBlazeDataPanel} to="/asset-catalog/transactions" className={'btn btn-primary'}>
        {this.props.t('dataModel.addServiceData')}
      </Link>
    );
  };

  getLinkToServiceDataDom = () => {
    return (
      <Row className={'justify-content-center'}>
        <Col lg={4}>
          <h2 className={styles.title + ' mb-4'}> {this.props.t('dataModel.noData')} </h2>
          <p className={'mb-5'}>{this.props.t('dataModel.noResults')}</p>
          {this.getAddDataButton()}
        </Col>
      </Row>
    );
  };

  /**
   * Creates action button for empty placeholder. Displayed button and action depends
   * on the context.
   */
  createPlaceholderCallToActionButton = () => {
    const tdmModel = this.state.dataModels.tdmModel;
    const modelsAreDifferent = this.state.dataModels.isDifferent;

    // No data entities have been created so far, models are same
    if (modelsAreDifferent === false && countEntitiesInDataModel(tdmModel) === 0) {
      return this.getAddDataButton();
    }

    // If there are no blaze data transactions used within a service
    if (this.state.isMsUsingBlazeData === false) {
      return (
        <Button
          onClick={() => {
            if (this.props.onTabChange) {
              this.props.onTabChange('main');
            }
          }}
        >
          {this.props.t('dataModel.configureTransactions')}
        </Button>
      );
    }

    // Models are different and transactions are using blaze data template in the virtual service
    if (modelsAreDifferent === true && this.state.isMsUsingBlazeData === true) {
      return <Button onClick={this.applyUpdatesHandler}>{this.props.t('dataModel.applyChangesFromSdm')}</Button>;
    }
  };

  getRunningWithoutDataPlaceholderDom = () => {
    return (
      <Row className={'justify-content-center'}>
        <Col lg={8}>
          <h2 className={styles.title + ' mb-4'}> {this.props.t('dataModel.noDataDeployed')} </h2>
          <p className={'mb-5'}>{this.props.t('dataModel.runningWithoutData')}</p>
          {this.createPlaceholderCallToActionButton()}
        </Col>
      </Row>
    );
  };

  /**
   * When iteration settings changes, we will propagate it up to the store first and then call refresh preview.
   * @param iterationOverrides
   */
  iterationOverrideHandler = async (iterationOverrides: IterationOverrides) => {
    await this.props.onUpdateIterationOverrides(iterationOverrides);
    await this.generateDataPreview(true);
    await this.checkModelComplexity();
  };

  getPlaceholder = () => {
    // During the merge of the draft, we stop rendering to avoid pre-mature render of no content placeholder
    // It might appear before the data is fetched
    if (this.isMockServiceInProgress()) {
      return;
    }

    //Deployed data not available yet
    let placeholderContent = this.getLinkToServiceDataDom();
    if (this.isFixedDataShown()) {
      placeholderContent = this.getRunningWithoutDataPlaceholderDom();
    }

    // No data model exists yet
    return <NoContentPlaceholder placeHolderContent={placeholderContent} height={300} iconSize={'100px'} />;
  };

  applyUpdatesHandler = () => {
    if (!isTemplate(this.props.mockServiceEntity)) {
      this.props.createDraft(this.props.mockServiceEntity as MockService);
    } else {
      this.props.createTemplateDraft(this.props.mockServiceEntity as MockServiceTemplate);
    }
    MixpanelService.track(APPLY_CHANGES_FROM_BLAZE_DATA_MODEL());
  };

  /**
   * Alert shown when the deployed data model differs from the one currently loaded
   * from blaze data.
   */
  dataModelsAreDifferentAlert = () => (
    <div className={'alert alert-warning mt-4'}>
      <Button
        onClick={this.applyUpdatesHandler}
        variant={'warning'}
        className={'float-right position-relative'}
        style={{ bottom: '5px' }}
      >
        {i18n.t('dataSettings:updateAlert.apply')}
      </Button>
      <BzmIcon variant={'icon-bzm-caution'} className={'alert-icon'} />
      <p
        className={'alert-message mb-0'}
        dangerouslySetInnerHTML={{ __html: i18n.t('dataSettings:updateAlert.newUpdatesAvailable') }}
      ></p>
    </div>
  );

  /**
   * Alert shown when there are no transactions leveraging blaze data.
   */
  blazeDataNotUsedAlert = () => (
    <div className="alert alert-info pb-0">
      <i className="icon icon-information alert-icon"></i>
      <p className="alert-message">
        This Virtual Service is currently configured without any data-driven Transactions, therefore you will not see
        any data after deploy.
      </p>
    </div>
  );

  /**
   * Starts export of the data from virtual service to shared folders.
   */
  requestStateChangesData = async () => {
    try {
      this.setState({ isExportInProgress: true });
      this.props.showToastNotification(ToastType.Info, 'Receiving data from Virtual Service is in progress...');

      //Initiate the process for getting data
      const msId = this.props.mockServiceEntity.id;
      const response = await acApi.mockServices.refreshBlazeData(msId);
      if (!response || !response.data || !response.data.result || !response.data.result.trackingId) {
        throw new Error(i18n.t('mockServices:errorMsgs.errorRetrevingTrackingId'));
      }
      const trackingId = response.data.result.trackingId;

      //Wait until data is in sharedFolder
      const trackingResult = await pollTrackingService(trackingId, 30, 3000);
      const errorMsg = _.get(trackingResult, 'data.result.errors');

      if (!_.isEmpty(errorMsg)) {
        throw new Error(errorMsg);
      }

      // Fetch all when finished
      await this.fetchAllData(true);
    } catch (error) {
      this.props.showToastNotification(
        ToastType.Danger,
        'Cannot Load data from Virtual Service',
        getErrorMessage(error),
      );
    } finally {
      this.setState({ isExportInProgress: false });
    }
  };

  getRefreshBtn = () => {
    const isMsStateful = this.props.isMsStateful !== undefined ? this.props.isMsStateful : false;

    if (isMsStateful && this.isFixedDataShown()) {
      return <RefreshBtn isExportInProgress={this.state.isExportInProgress} onClick={this.requestStateChangesData} />;
    }

    return <></>;
  };

  getDataPreviewForCurrentEntity = () => {
    const currentPreview = _.get(this.state.dataPreviews, this.props.dataEntitySelected);
    if (_.isNil(currentPreview)) {
      return getDefaultDataPreview(true);
    }
    return currentPreview;
  };

  /**
   * Gets main dom with content after model is loaded.
   */
  getMainContent = () => {
    //Do not display content...
    const hasNoEntities = countEntitiesInDataModel(this.state.selectedDataModel) === 0;
    const hasNoGlobalEntity = _.isEmpty(this.state.globalScope);
    if (this.isGeneratingData() || (hasNoEntities && hasNoGlobalEntity)) {
      return <div style={{ marginTop: '100px' }}>{this.getPlaceholder()}</div>;
    }

    //Get overrides from the virtual service object
    const iterationOverrides = _.get(
      this.props.mockServiceEntity,
      'dataSettings.data.attributes.iterationOverrides.entities',
    );

    return (
      <>
        {/*Models differ alert*/}
        {this.state.dataModels.isDifferent && this.dataModelsAreDifferentAlert()}

        {/* Warning Alerts */}
        {!this.isFixedDataShown() && (this.state.isComplex || this.state.isLimitApplied) && (
          <div className="alert alert-warning pb-0">
            <BzmIcon variant={'icon-bzm-caution'} className={'alert-icon'} />
            {this.state.isComplex && (
              <p className="alert-message">{i18n.t('dataSettings:dataModel.longProcessingAlert')}</p>
            )}
            {this.state.isLimitApplied && (
              <p
                className="alert-message"
                dangerouslySetInnerHTML={{
                  __html: i18n.t('dataSettings:dataModel.limitApplied', {
                    blazeDataLimit: getFriendlyNumberFormat(this.state.blazeDataLimit),
                    billingUrl: getBlazemeterBillingPageUrl(),
                  }),
                }}
              ></p>
            )}
          </div>
        )}

        {/*Info Alert */}
        {!this.isFixedDataShown() && this.state.isMsUsingBlazeData === false && (
          <div className="alert alert-info pb-0">
            <i className="icon icon-information alert-icon"></i>
            {this.state.isMsUsingBlazeData === false && (
              <p className="alert-message">{i18n.t('dataSettings:dataModel.noTransactionsWithBlazeData')}</p>
            )}
          </div>
        )}
        {/*TODO validate this: this.props.mockServiceEntity as MockService*/}
        <Row>
          <Col lg={4} xl={3}>
            <BlazeDataEntitiesAccordion
              mockService={this.props.mockServiceEntity}
              dataModel={this.state.selectedDataModel}
              activeEntity={this.props.dataEntitySelected}
              onUpdateIterationOverrides={this.iterationOverrideHandler}
              iterationOverrides={iterationOverrides}
              onActiveEntityChanged={async (entityId?: string) => {
                this.setCurrentEntity(entityId ? entityId : undefined);
                if (entityId !== GLOBAL_SCOPE_KEY && entityId !== SQL_BOOTSTRAP_SCRIPT) {
                  this.generateDataPreview(false, entityId);
                }
              }}
              disabled={
                this.props.disabled || (this.state.dataModels.isDifferent && !this.props.mockServiceEntity.isDraft)
              }
            />
          </Col>
          <Col lg={8} xl={9}>
            {this.props.dataEntitySelected === GLOBAL_SCOPE_KEY ? (
              <GlobalScopePreview
                globalScope={this.state.globalScope}
                dataCount={_.size(this.state.globalScope)}
                refreshBtn={this.getRefreshBtn()}
                isExportInProgress={this.state.isExportInProgress}
                isFixedData={this.isFixedDataShown()}
              />
            ) : this.props.dataEntitySelected === SQL_BOOTSTRAP_SCRIPT ? (
              <SqlBootstrapForm mockService={this.props.mockServiceEntity as MockService} />
            ) : (
              <MockServicePreviewDataSample
                key={'entity-preview-' + this.props.dataEntitySelected}
                dataModel={this.state.selectedDataModel}
                activeEntity={this.props.dataEntitySelected}
                manipulatorDataModel={this.state.selectedManipulatorDataModel}
                dataPreview={this.getDataPreviewForCurrentEntity()}
                isLoading={this.state.isGeneratingDataPreview}
                refreshBtn={this.getRefreshBtn()}
                isExportInProgress={this.state.isExportInProgress}
                isError={this.state.isError}
              />
            )}
          </Col>
        </Row>
      </>
    );
  };

  /**
   * Virtual Service is in Progress (Generating data, being deployed etc...)
   */
  isMockServiceInProgress = () => {
    const metaInProgress = _.get(this.props.mockServiceEntity, 'metaData.inProgress');
    const isConfiguring = _.get(this.props.mockServiceEntity, 'status') === 'CONFIGURING';
    return metaInProgress || isConfiguring;
  };

  /**
   * Data generation is in progress (after stop is triggered)
   */
  isGeneratingData = () => {
    const msAction = _.get(this.props.mockServiceEntity, 'metaData.inProgressAction');
    return this.isMockServiceInProgress() && msAction !== 'MERGE' && msAction !== 'STOP';
  };

  /** Shows in progress message based on the current action. */
  getInProgressMessage = () => {
    return this.isGeneratingData() ? i18n.t('dataSettings:generating') : i18n.t('common:simpleLoader.pleaseWait');
  };

  render() {
    const isInProgress = this.isMockServiceInProgress();
    const isFetchingData = this.state.isFetchingDataModel === true;
    const isLoadingGlobalScope = this.state.globalScope === null;

    return (
      <div className="position-relative" style={{ minHeight: 400 }}>
        {isFetchingData || isLoadingGlobalScope || isInProgress ? (
          <div className="position-relative" style={{ minHeight: 400 }}>
            <div className="blur-background">
              <FullPageLoader
                loadingText={isInProgress ? this.getInProgressMessage() : i18n.t('dataSettings:loading')}
              />
            </div>
          </div>
        ) : (
          this.getMainContent()
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ ui, blazeUserConfig }: ApplicationState) => ({
  isDDPanelOpened: ui.isDDPanelOpened,
  dataEntitySelected: ui.dataEntitySelected,
  appState: {
    blazeUserConfig,
  },
});

const mapDispatchToProps = {
  setPageFilter,
  toggleOpenDDPanel,
  showToastNotification,
  createDraft,
  createTemplateDraft,
  setDataEntitySelected,
};

type MapToStateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('dataSettings')(connect(mapStateToProps, mapDispatchToProps)(MockServiceDataSettings));
